/**
 * The company identifier a tender needs: an ABN, or an ACN when there is no ABN.
 *
 * ⚠️ REQUIRING AN ABN FORCED PEOPLE TO INVENT ONE.
 *
 * Step 1 would not continue without an eleven-digit, checksum-valid ABN. Plenty of entities have
 * only an ACN — QBG GROUP PTY LTD came in from Salesforce with ACN 621324785 and no ABN at all —
 * so the only way past the form was to type a plausible-looking ABN and delete it later.
 *
 * Deleting it later does not help. Step 1 has already written client.abn, and generation reads
 * `client_abn` straight from that row, so the invented number reaches the client's proposal.
 * Reported as: "I had to use a random ABN to proceed and then remove it at the processing email
 * stage. However, the random ABN might still appear in the generated proposal."
 *
 * So the rule becomes "one identifier, either kind". An ABN is still checksum-validated when one
 * is given — this loosens WHICH identifier is required, never how an ABN itself is checked.
 */

/** Digits only. Both numbers are quoted with spaces, and Salesforce sends them unpadded. */
const digitsOf = (value: string | null | undefined): string =>
  typeof value === 'string' ? value.replace(/\D/g, '') : '';

export const ABN_LENGTH = 11;
export const ACN_LENGTH = 9;

/**
 * ⚠️ LENGTH ONLY — THE CHECKSUM IS NOT RE-IMPLEMENTED HERE.
 *
 * ABN validity is already decided by validateABN, which calls the ABR lookup and holds the
 * authoritative answer. This asks the narrower question the form gate needs: is there an ABN to
 * validate at all?
 */
export const hasAbnShape = (abn: string | null | undefined): boolean =>
  digitsOf(abn).length === ABN_LENGTH;

/**
 * An ACN is nine digits.
 *
 * No checksum is applied. The ACN check digit exists, but ASIC issues numbers that predate it and
 * Salesforce stores some unpadded; refusing those would recreate the very problem this fixes —
 * a correct company blocked by a format rule. Length is the honest limit of what can be asserted
 * without a lookup.
 */
export const hasAcnShape = (acn: string | null | undefined): boolean =>
  digitsOf(acn).length === ACN_LENGTH;

/**
 * May the tender proceed?
 *
 * `abnConfirmedValid` is validateABN's verdict. An ABN that is present but failed its lookup does
 * NOT satisfy the requirement on its own — but an ACN alongside it still does, so a mistyped ABN
 * never blocks a company that has given a valid ACN.
 */
export const hasCompanyIdentifier = (
  abn: string | null | undefined,
  acn: string | null | undefined,
  abnConfirmedValid: boolean,
): boolean => (hasAbnShape(abn) && abnConfirmedValid) || hasAcnShape(acn);

/** What to tell someone who has given neither. */
export const companyIdentifierError = 'Valid ABN (11 digits) or ACN (9 digits)';

/**
 * The identifier a proposal should print, and its label.
 *
 * ABN wins when there is one, because that is what the retailers and the client expect to see.
 * An ACN-only company gets an ACN line instead of an "ABN: N/A" that says nothing — and, before
 * this, instead of an invented ABN that said something false.
 */
export const displayIdentifier = (
  abn: string | null | undefined,
  acn: string | null | undefined,
): { label: 'ABN' | 'ACN'; value: string } | null => {
  if (hasAbnShape(abn)) return { label: 'ABN', value: digitsOf(abn) };
  if (hasAcnShape(acn)) return { label: 'ACN', value: digitsOf(acn) };
  return null;
};
